import { fairUseRequest } from "@/api/admin/fair-use"
import { api } from "@/lib/api-client"

// =============================================================================
// Billing months — closing a month and exporting its billing basis.
//
// Platform-admin only. Both routes answer with the SAME `{error, code}`
// envelope as /api/admin/fair-use/* (`platform_admin_required`,
// `month_locked`, `month_in_future`), so they run through
// {@link fairUseRequest} and surface as `FairUseApiError` — one error
// vocabulary for the whole ops card.
// =============================================================================

/** A billing month after a lock attempt. */
export type BillingMonthLock = {
  year_month: string
  locked: boolean
  locked_at: string | null
}

/**
 * Close a billing month.
 *
 * Irreversible from the UI: once locked, no grant, approval or revoke may
 * change what that month invoices (the fair-use routes answer 409
 * `month_locked`), so the caller must confirm first.
 */
export async function lockBillingMonth(yearMonth: string): Promise<BillingMonthLock> {
  return fairUseRequest(() =>
    api.post(`/admin/billing/months/${encodeURIComponent(yearMonth)}/lock`, {})
  )
}

/** The URL the billing-basis CSV is served from, for one month. */
export function billingExportPath(yearMonth: string): string {
  return `/admin/billing/months/${encodeURIComponent(yearMonth)}/export.csv`
}

/**
 * Download the month's billing basis as a real file.
 *
 * Goes through the authenticated axios client rather than a plain `<a href>`:
 * the route is bearer-authorized, so a naked link would hit EasyAuth and
 * return an HTML sign-in page saved as "export.csv". The response interceptor
 * unwraps `response.data`, which for `responseType: "blob"` IS the Blob.
 */
export async function downloadBillingExport(yearMonth: string): Promise<void> {
  const blob = await fairUseRequest<Blob>(() =>
    api.get(billingExportPath(yearMonth), { responseType: "blob" })
  )
  const { saveAs } = await import("file-saver")
  saveAs(blob, `ai-budget-${yearMonth}.csv`)
}
